import { Input } from "@screener/uikit/components/input";
import { PanelBlock } from "@screener/uikit/components/panel";
import { cn } from "@screener/uikit/lib/utils";
import type { ReactNode } from "react";
import { ExchangeLogo } from "@/density/exchange-logo";
import { exchangeLabel } from "@/lib/labels";
import { marketRowLabel } from "./terminal-settings-draft";

/**
 * Таблица «Соответствие бирж и рынков» (DEN-162): строка — биржа, колонки —
 * пары значений для PERP и SPOT. Рынка у биржи нет в строках черновика —
 * вместо пары «рынка нет». Поля подписаны названием рынка и колонки
 * (`Binance · PERP: e`), как до редизайна. Общая для TigerTrade (`e`, `m`)
 * и MetaScalp (`exchange`, `market`): различаются подписи колонок и вид ввода.
 */

const COLUMNS =
  "grid-cols-[minmax(120px,1fr)_repeat(2,minmax(0,104px))_1px_repeat(2,minmax(0,104px))]";
const MARKETS = ["perp", "spot"] as const;
type MarketKind = (typeof MARKETS)[number];

export interface MappingTableRow {
  /** `"<exchange>:<market>"` — ключ строки черновика. */
  key: string;
  first: string;
  second: string;
  /** Ячейки пары подсвечены: заполнена одна (неполная пара), либо код неверен. */
  flagged: boolean;
}

export interface MappingTableProps {
  title: string;
  description: string;
  rows: readonly MappingTableRow[];
  /** Подписи колонок пары: `e`/`m` или `exchange`/`market`. */
  columns: readonly [string, string];
  /** Поле числовое (MetaScalp): цифровая клавиатура; подсветка пары — ошибка, а не неполнота. */
  numeric?: boolean;
  onChange: (key: string, patch: { first?: string; second?: string }) => void;
  /** Пояснение под таблицей к подсвеченным парам; получает названия рынков («Binance · PERP»). */
  flaggedNote?: (labels: readonly string[]) => string;
  errorSlot?: ReactNode;
}

function splitKey(key: string): { exchange: string; market: string } {
  const [exchange = "", market = ""] = key.split(":");
  return { exchange, market };
}

/** Число рынков, где заполнены оба значения пары. */
function filledCount(rows: readonly MappingTableRow[]): number {
  return rows.filter((row) => row.first.trim() !== "" && row.second.trim() !== "").length;
}

export function MappingTable({
  title,
  description,
  rows,
  columns,
  numeric = false,
  onChange,
  flaggedNote,
  errorSlot,
}: MappingTableProps) {
  const exchanges: string[] = [];
  const byKey = new Map<string, MappingTableRow>();
  for (const row of rows) {
    const { exchange } = splitKey(row.key);
    if (!exchanges.includes(exchange)) {
      exchanges.push(exchange);
    }
    byKey.set(row.key, row);
  }
  const flaggedLabels = rows.filter((row) => row.flagged).map((row) => marketRowLabel(row.key));

  return (
    <PanelBlock>
      <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
        <legend className="sr-only">{title}</legend>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <div aria-hidden="true" className="text-[13px] leading-[1.3] font-medium">
              {title}
            </div>
            <p className="text-xs leading-[1.45] text-pretty text-muted-foreground">
              {description}
            </p>
          </div>
          <span className="shrink-0 text-[11px] font-medium tracking-wide text-muted-foreground uppercase tabular-nums">
            <span className="text-foreground">{filledCount(rows)}</span> из {rows.length} рынков
          </span>
        </div>
        {errorSlot}
        <div className="relative overflow-x-auto rounded-lg border border-border">
          <div className="min-w-[560px]">
            <div className="border-b border-border bg-background px-2.5">
              <div className={cn("grid items-center gap-x-1.5", COLUMNS)}>
                <span className="row-span-2 text-[11px] tracking-wide text-muted-foreground uppercase">
                  Биржа
                </span>
                <span className="col-span-2 pt-1.5 text-[10px] font-semibold tracking-[.08em]">
                  PERP
                </span>
                <span
                  className="row-span-2 h-full w-px self-stretch bg-border"
                  aria-hidden="true"
                />
                <span className="col-span-2 pt-1.5 text-[10px] font-semibold tracking-[.08em]">
                  SPOT
                </span>
                {MARKETS.flatMap((market) =>
                  columns.map((column) => (
                    <span
                      key={`${market}-${column}`}
                      className="pb-1.5 text-[11px] font-medium text-muted-foreground"
                    >
                      {column}
                    </span>
                  )),
                )}
              </div>
            </div>
            {exchanges.map((exchange) => (
              <div
                key={exchange}
                className={cn(
                  "grid min-h-[38px] items-center gap-x-1.5 border-t border-border px-2.5 first:border-t-0 hover:bg-muted/40",
                  COLUMNS,
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <ExchangeLogo exchange={exchange} decorative className="h-4 shrink-0" />
                  <span className="truncate text-[13px] font-medium">
                    {exchangeLabel(exchange)}
                  </span>
                </span>
                <PairCells
                  row={byKey.get(`${exchange}:perp`)}
                  market="perp"
                  columns={columns}
                  numeric={numeric}
                  onChange={onChange}
                />
                <span className="h-full w-px bg-border" aria-hidden="true" />
                <PairCells
                  row={byKey.get(`${exchange}:spot`)}
                  market="spot"
                  columns={columns}
                  numeric={numeric}
                  onChange={onChange}
                />
              </div>
            ))}
          </div>
        </div>
        {flaggedLabels.length > 0 && flaggedNote ? (
          <p className="flex items-center gap-2 text-xs text-warning">
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-[3px] border border-warning bg-warning-wash"
            />
            {flaggedNote(flaggedLabels)}
          </p>
        ) : null}
      </fieldset>
    </PanelBlock>
  );
}

function PairCells({
  row,
  market,
  columns,
  numeric,
  onChange,
}: {
  row: MappingTableRow | undefined;
  market: MarketKind;
  columns: readonly [string, string];
  numeric: boolean;
  onChange: MappingTableProps["onChange"];
}) {
  if (row === undefined) {
    return (
      <span className="col-span-2 text-xs text-muted-foreground/60">
        рынка нет
        <span className="sr-only"> ({market})</span>
      </span>
    );
  }
  const label = marketRowLabel(row.key);
  const cellClass = cn(
    "h-[26px] rounded-md px-2 text-xs font-medium tabular-nums",
    row.flagged && !numeric
      ? "border-warning bg-warning-wash aria-invalid:border-warning aria-invalid:ring-warning/20 dark:aria-invalid:border-warning dark:aria-invalid:ring-warning/20"
      : "",
  );
  return (
    <>
      <Input
        aria-label={`${label}: ${columns[0]}`}
        aria-invalid={row.flagged ? true : undefined}
        value={row.first}
        placeholder="—"
        inputMode={numeric ? "numeric" : undefined}
        autoComplete="off"
        className={cellClass}
        onChange={(event) => onChange(row.key, { first: event.target.value })}
      />
      <Input
        aria-label={`${label}: ${columns[1]}`}
        aria-invalid={row.flagged ? true : undefined}
        value={row.second}
        placeholder="—"
        inputMode={numeric ? "numeric" : undefined}
        autoComplete="off"
        className={cellClass}
        onChange={(event) => onChange(row.key, { second: event.target.value })}
      />
    </>
  );
}
